import React, { useRef } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import styled from '@emotion/styled';
import { BREAKPOINTS } from 'Style/breakpoints';
import { SPACING } from 'Style/spacing';
import { BODY_TYPES } from 'Style/typography';
import {
  TEXT_COLORS,
  DIVIDER_COLORS,
  DefaultTextColorStyle,
} from 'Style/colors';

import { USAGE_NAV_FROMS } from 'Utils/analytics/usage';
import StoryboardRenderingUtil from 'Utils/content/storyboard-rendering-util';
import sentenceCase from 'Utils/content/sentence-case';
import SectionUtil from 'Utils/content/section-util';

import { NavFromContext } from 'ComponentLibrary/context';

import StoryboardHeader from './storyboard-header';
import StoryboardFooter from './storyboard-footer';
import StoryboardGroup from './storyboard-group';
import StoryboardPost from './post';
import { StoryboardPostExcerpt } from 'Webapp/shared/app/themes/storyboard-layout/post-text';
import ContextSection from '../../components/context-section';
import StoryboardAd from '../../components/ads/storyboard-ad';
import LoadMoreScrollToBottomTracker from '../../components/load-more-scroll-to-bottom-tracker';
import VisibilityScrollToBottomTracker from '../../components/visibility-scroll-to-bottom-tracker';
import { Meatball } from 'ComponentLibrary/icons/meatball';

import withScrollOverlay from '../../hocs/withScrollOverlay';
import withT from 'ComponentLibrary/hocs/withT';

import connector from 'Utils/connector';
import connectAmp from 'Webapp/shared/app/connectors/connectAmp';
import connectAuthentication from 'Webapp/shared/app/connectors/connectAuthentication';
import connectResponsive from 'Webapp/shared/app/connectors/connectResponsive';

const RELATED_ARTICLES_LIMIT = 6;

const StoryboardMain = styled.main({
  height: 'initial',
  minHeight: 'initial',
});
const ThemedStoryboardCuratorContentStyle = {
  backgroundColor: 'var(--color--static-near-black)', // Overridden in code by cover item
  [StoryboardPostExcerpt]: DefaultTextColorStyle(true),
  '.ui-text--supporting': {
    // TODO: const contentsClassName in source.js needs to be styled to remove this line
    // /src/shared/app/components/attribution/source.js
    color: TEXT_COLORS.secondaryReverse,
  },
  'a:hover': {
    color: TEXT_COLORS.overlay,
  },
  [Meatball]: {
    backgroundColor: TEXT_COLORS.overlay,
  },
};

const StyledStoryBoardItem = styled.section({
  '>*': {
    '@media (max-width: 992px)': {
      paddingLeft: SPACING.LARGE,
      paddingRight: SPACING.LARGE,
    },
  },
});

const InlineStoryboardCuratorContentStyle = {
  overflow: 'unset',

  // Divider between two top level storyboard elements that are NOT both items.
  '.storyboard-group+.storyboard-group, .storyboard-item+.storyboard-group, .storyboard-group+.storyboard-item':
    {
      borderTopColor: DIVIDER_COLORS.primary,
      ...BREAKPOINTS.phone({
        borderTopColor: DIVIDER_COLORS.primary,
      }),
    },

  // Divider between two items within a storyboard group/section
  '.storyboard-group>div+div': {
    ...BREAKPOINTS.phone({
      borderTopColor: DIVIDER_COLORS.primary,
    }),
  },

  // Divider betweeen two top level storyboard items.
  '.storyboard-item+.storyboard-item': {
    ...BREAKPOINTS.phone({
      borderTopColor: DIVIDER_COLORS.primary,
    }),
  },
};

const applyStoryboardCuratorContentBodyStyle = function (props) {
  return props.isInline
    ? InlineStoryboardCuratorContentStyle
    : ThemedStoryboardCuratorContentStyle;
};
/**
 * shouldForwardProp will be evaluated against each prop and only pass it to
 * the component if true
 */
const shouldForwardProp = (prop) => prop !== 'isInline';

const StoryboardCuratorContent = styled('div', { shouldForwardProp })(
  {
    position: 'relative',
    overflow: 'auto',

    '.storyboard_group': {
      '.ui-text--supporting': {
        ...BODY_TYPES.LARGE_STANDARD,
      },
    },

    // Divider between two top level storyboard elements that are NOT both items.
    '.storyboard-group+.storyboard-group, .storyboard-item+.storyboard-group, .storyboard-group+.storyboard-item':
      {
        borderTop: '2px solid',
        ...BREAKPOINTS.tabletPortraitUp({
          marginTop: SPACING.BASE6X,
          paddingTop: SPACING.BASE6X,
        }),
        ...BREAKPOINTS.phone({
          marginTop: SPACING.LARGE,
          paddingTop: SPACING.BASE4X,
          borderTop: `${SPACING.XLARGE} solid`,
        }),
      },

    '.storyboard-post': {
      margin: '0px',
      ...BREAKPOINTS.phone({
        paddingTop: SPACING.XLARGE,
        paddingBottom: SPACING.LARGE,
      }),
    },

    '.storyboard-post--small': {
      ...BREAKPOINTS.phone({
        paddingTop: SPACING.LARGE,
        paddingBottom: SPACING.LARGE,
      }),
    },
  },
  applyStoryboardCuratorContentBodyStyle,
);

const StoryboardBodyContent = styled.div(
  {
    alignContent: 'center',
    '@media (min-width: 992px)': {
      marginLeft: 'auto',
      marginRight: 'auto',
      maxWidth: '960px',
    },
    '.item-list--grid': {
      gap: SPACING.XLARGE,
    },
  },
  BREAKPOINTS.tabletPortraitUp({
    marginTop: SPACING.BASE6X,
    marginBottom: SPACING.BASE6X,
  }),
  BREAKPOINTS.phone({
    marginTop: SPACING.XLARGE,
    marginBottom: SPACING.BASE4X,
  }),
);

const StyledContextSection = styled(ContextSection)({
  '>*': {
    '@media (max-width: 992px)': {
      paddingLeft: SPACING.LARGE,
      paddingRight: SPACING.LARGE,
    },
  },

  '.article__section-footer--see-more': {
    textAlign: 'center',
    marginTop: SPACING.BASE6X,
  },
});

const StyledStoryBoardItemDividerSelector = `${[StyledStoryBoardItem]} + ${[
  StyledStoryBoardItem,
]}`;

const StyledStoryBoardGroups = styled.ul({
  // Divider betweeen two top level storyboard items.
  [StyledStoryBoardItemDividerSelector]: {
    ...BREAKPOINTS.tabletPortraitUp({
      paddingTop: SPACING.XLARGE,
    }),
    ...BREAKPOINTS.phone({
      borderTop: '2px solid',
    }),
  },
});

function StoryboardLayout({
  section,
  items,
  isAmp,
  loadNextPage,
  isLoading,
  neverLoadMore,
  relatedArticlesSection,
  relatedTopicsSections,
  isAuthenticated,
  isPhone,
  t,
  renderScrollOverlay,
  usageTrackReachedSectionEnd,
  isInline,
}) {
  if (items.size === 0) {
    return null;
  }

  const storyboardBodyRef = useRef();

  const renderableItems = items.filter((i) => !i.isSectionCover);
  const coverItem = items.find((item) => item.isSectionCover);
  const coverImage = StoryboardRenderingUtil.getCoverImage(coverItem, section);
  const backgroundStyle = isInline
    ? {}
    : StoryboardRenderingUtil.getBackgroundColorStyle(coverImage, 1);
  const dividerStyle = isInline
    ? {}
    : StoryboardRenderingUtil.getBorderColorStyle(coverImage) || {
        borderColor: DIVIDER_COLORS.newStoryboard,
      };

  const mainClassName = classNames({
    amp: isAmp,
  });
  const curatorSectionClassName = classNames({
    'storyboard-layout-curator': true,
    'storyboard-layout-curator-storyboard-theme': !isInline,
    'storyboard-layout-curator-inline': isInline,
  });

  const renderMainStoryboard = () => {
    const bodyY = storyboardBodyRef.current
      ? storyboardBodyRef.current.getBoundingClientRect().y
      : 0;
    const itemCount =
      SectionUtil.getMetricsCountValue(section, 'articles') || 0;
    return (
      <StoryboardMain className={mainClassName} data-navbar-style="dark">
        <StoryboardCuratorContent
          className={curatorSectionClassName}
          style={backgroundStyle}
          isInline={isInline}
        >
          {!isPhone &&
            !isAmp &&
            !isAuthenticated &&
            !isInline &&
            renderScrollOverlay(bodyY, itemCount, t('storyboard'))}
          <StoryboardBodyContent
            className="storyboard-layout-redesigned"
            ref={storyboardBodyRef}
          >
            <StyledStoryBoardGroups>
              {/* One in-body ad at the TOP of the storyboard body (right under
                  the cover, above the articles) — high viewability, and it
                  doesn't get buried at the end of a short storyboard. Full
                  non-AMP storyboards only; StoryboardAd renders nothing unless
                  the storyboard-ads flags are on and the section carries a dfp
                  adhints unit. AMP is excluded (no GPT lifecycle) so the
                  reserved ad container never leaves permanent blank space. */}
              {!isInline && !isAmp && (
                <StoryboardAd key="storyboard-ad" section={section} />
              )}
              {renderableItems.map((item, index) => {
                if (item.isGroup) {
                  return (
                    <StoryboardGroup
                      key={item.id}
                      group={item}
                      section={section}
                      styleOverride={dividerStyle}
                      postStyleOverride={dividerStyle}
                      isInline={isInline}
                    />
                  );
                }

                return (
                  <StyledStoryBoardItem
                    className="storyboard-item"
                    style={dividerStyle}
                    key={item.id || item.title}
                  >
                    <StoryboardPost
                      item={item}
                      section={section}
                      index={index}
                      isInline={isInline}
                    />
                  </StyledStoryBoardItem>
                );
              })}
            </StyledStoryBoardGroups>
            {!isInline && (
              // Handle by parent wrapping section.
              <React.Fragment>
                {!neverLoadMore && !isAmp && (
                  <LoadMoreScrollToBottomTracker
                    loadNext={loadNextPage}
                    isLoading={isLoading}
                    immediateInfinite
                    onScrolledToBottom={() =>
                      usageTrackReachedSectionEnd(section)
                    }
                  />
                )}
                {neverLoadMore && (
                  <VisibilityScrollToBottomTracker
                    onScrolledToBottom={() =>
                      usageTrackReachedSectionEnd(section)
                    }
                  />
                )}
              </React.Fragment>
            )}
          </StoryboardBodyContent>
        </StoryboardCuratorContent>
      </StoryboardMain>
    );
  };

  const renderRelatedSections = () => (
    <div>
      {relatedArticlesSection && relatedArticlesSection.items.length > 0 && (
        <StoryboardBodyContent className="storyboard-layout-redesigned">
          <StyledContextSection
            section={relatedArticlesSection}
            sectionItemLimit={RELATED_ARTICLES_LIMIT}
            headerOverride={sentenceCase(t('related_articles'))}
            condensedItems
          />
        </StoryboardBodyContent>
      )}
      {relatedTopicsSections.map(
        (relatedTopicSection) =>
          relatedTopicSection &&
          relatedTopicSection.items &&
          relatedTopicSection.items.length >= RELATED_ARTICLES_LIMIT && (
            <StoryboardBodyContent
              key={relatedTopicSection.title}
              className="storyboard-layout-redesigned"
            >
              <StyledContextSection
                section={relatedTopicSection}
                sectionItemLimit={RELATED_ARTICLES_LIMIT}
                condensedItems
              />
            </StoryboardBodyContent>
          ),
      )}
    </div>
  );

  return (
    <NavFromContext.Provider value={USAGE_NAV_FROMS.NGL}>
      <StoryboardHeader
        section={section}
        coverItem={coverItem}
        isInline={isInline}
      />
      {renderMainStoryboard()}
      {!isInline && (
        <StoryboardFooter section={section} coverItem={coverItem} />
      )}
      {!isAuthenticated && !isAmp && renderRelatedSections()}
    </NavFromContext.Provider>
  );
}

StoryboardLayout.propTypes = {
  section: PropTypes.object.isRequired,
  items: PropTypes.array.isRequired,
  isAmp: PropTypes.bool,
  relatedArticlesSection: PropTypes.object,
  relatedTopicsSections: PropTypes.arrayOf(PropTypes.object),
  loadNextPage: PropTypes.func.isRequired,
  neverLoadMore: PropTypes.bool,
  isLoading: PropTypes.bool,
  t: PropTypes.func.isRequired,
  isAuthenticated: PropTypes.bool.isRequired,
  renderScrollOverlay: PropTypes.func.isRequired,
  usageTrackReachedSectionEnd: PropTypes.func.isRequired,
  isPhone: PropTypes.bool.isRequired,
  isInline: PropTypes.bool,
};

StoryboardLayout.defaultProps = {
  neverLoadMore: false,
  isAmp: false,
  isLoading: false,
  relatedArticlesSection: null,
  relatedTopicsSections: [],
  isInline: false,
};

export default connector(
  connectResponsive,
  connectAuthentication,
  connectAmp,
)(withScrollOverlay(withT(StoryboardLayout)));
